import { Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
	getIdsFromListData,
	getNewParams,
	updateRouterData,
	paramsHash,
	prepareFilters,
	prepareSorting,
	getAllProducts,
	getNewSortingParams,
	getNewFilterParams
} from 'functions/plpOps';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { getCMSSettings } from 'tools/cmsBlockSettings';

// Standard PLP elements
import ProductFilters from 'components/filters/left-filters';
import ProductSorting from "./sorting";
import ProductInfiniteScroll from 'components/navigation/infinite-scroll';
import ProductGrid from 'components/products/swGrid';
import { ScrollContext } from 'components/providers/scroll';
import { TrackingContext } from 'components/providers/tracking';
import { useTranslation } from 'tools/i18n';
import BottomSection from './bottom-section';
import useSessionStore from '@services/stores/SessionStore';

const useStyles = makeStyles(
	(theme) => ({
		wrapper: {
			position: 'relative',
			paddingTop: 0,
			paddingBottom: 40,
			backgroundColor: theme.palette.primary.background
		},
		sortingWrapper: {

		},
		filterGridWrapper: {
			display: 'flex',
			justifyContent: 'center',
			gap: 24,
			paddingTop: 20,
			['@media only screen and (max-width: 1524px)']: {
				justifyContent: 'flex-end',
			},
			[theme.breakpoints.down('lg')]: {
				paddingLeft: 20,
				paddingRight: 20
			},
			[theme.breakpoints.down('md')]: {
				flexDirection: 'column',
				paddingLeft: 0,
				paddingRight: 0
			},
			[theme.breakpoints.up('lg')]: {
				padding: '0 24px',
				'@media screen and (min-width: 1296px)': {
					'&:after': {
						content: '""',
						width: 'calc(((100% - 1600px) / 2) - 24px)',
						maxWidth: 300,
						flexShrink: 0
					}
				}
			}
		},
		gridWrapper: {
			width: '75%',
			flexGrow: 1,
			margin: 0,
			[theme.breakpoints.down('md')]: {
				width: '100%'
			},
		}
	}),
	{ name: 'StandardPLP' }
);

function StandardPLP({ block, breadcrumbs, category, categoryMenu }) {
	const { t } = useTranslation();
	const classes = useStyles();
	const { actions } = useContext(TrackingContext);
	const { setCache, getCache, isPopState } = useContext(ScrollContext);
	const CMSSettings = getCMSSettings(block);
	const prodListData = block.slots[0];
	const productListing = prodListData.data ? prodListData.data.listing : { elements: [], limit: 48, page: 1 };
	const currentFilters = prodListData.data ? prodListData.data.listing.currentFilters : {};
	const defaultSorting = prodListData.data ? prodListData.data.listing.sorting : 'topseller';
	const availableFilters = prodListData.data ? prodListData.data.listing.aggregations : {};
	const availableSortings = prodListData.data ? prodListData.data.listing.availableSortings : [];

	const session = useSessionStore((state) => state.session);
	const router = useRouter();
	const { query, asPath } = router;
	const [initial, setInitial] = useState(true);
	const [products, setProducts] = useState(productListing.elements);
	const [pagination, setPagination] = useState({
		initialPage: productListing.page,
		loadedPages: [productListing.page]
	});
	const [categorySlug, setCategorySlug] = useState(asPath.split('?')[0]);
	const { page, order, slug, ...filters } = query;
	const filterOpen = query.filter === 'open';

	useEffect(() => {
		let newCategoryPath = asPath.split('?')[0];

		if (initial || newCategoryPath !== categorySlug) {
			setInitial(true);
			setCategorySlug(asPath.split('?')[0]);
			setPagination({ initialPage: parseInt(query.page) || 1, loadedPages: [parseInt(query.page) || 1] });
		}
	}, [asPath]);

	useEffect(() => {
		if (isPopState && initial) {
			let cachedCategory = getCache(router.asPath);

			if (cachedCategory) {
				setInitial(false);
				setPagination(cachedCategory.pagination);
				setProducts(cachedCategory.products);
			}
		}
	}, [asPath]);

	useEffect(() => {
		if (!initial) {
			// Update pagination with the now loaded page
			let loadedPages = pagination.loadedPages;
			if (loadedPages.indexOf(productListing.page) < 0) {
				if (productListing.page > pagination.initialPage) {
					loadedPages.push(productListing.page);
				} else if (productListing.page) {
					loadedPages.unshift(productListing.page);
				}

				setPagination({ ...pagination, loadedPages });
			}

			setProducts(getAllProducts(products, prodListData, pagination));

			/**
			 * Save products to localstorage for custom history state management
			 */
			let stateKey = router.asPath;
			setCache(stateKey, { pagination, products: getAllProducts(products, prodListData, pagination) });
		} else {
			if(isPopState) {
				setPagination({ initialPage: parseInt(query.page) || 1, loadedPages: [parseInt(query.page) || 1] });
			}

			setProducts(prodListData.data.listing.elements);
		}
	}, [prodListData]);

	useEffect(() => {
		setPagination(pagination => {
			return { ...pagination, currentPage: parseInt(page) || 1 };
		});
	}, [page]);

	function onSortChange(data) {
		setInitial(true);
		updateUrlParams({ ...getNewSortingParams(page, query, data) });
		setPagination({ initialPage: 1, loadedPages: [1] });
	}

	function onFilterChange(attribute, data) {
		setInitial(true);
		setPagination({ initialPage: 1, loadedPages: [1] });
		updateUrlParams({ ...getNewFilterParams(page, query, attribute, data, availableFilters) });
	}

	function onPageChange(value) {
		setInitial(false);
		updateUrlParams({ ...getNewParams(value, query) });
	}

	function updateUrlParams({ page, order, slug, ...filters }) {
		const { routerData, replace } = updateRouterData({
			page,
			order,
			initialPage: pagination.initialPage,
			pathname: router.asPath.split('?')[0],
			filters,
			availableFilters
		});

		if (replace) {
			router.replace(routerData, undefined, { scroll: false }).catch(e => console.log(e));
		} else {
			router.push(routerData, undefined, { scroll: false }).catch(e => console.log(e));
		}
	}

	return (
		<>
			<div className={classes.wrapper}>
				<div className={classes.sortingWrapper}>
					<ProductSorting
						block={block}
						sortings={prepareSorting(availableSortings)}
						currentSorting={order || defaultSorting}
						onSortChange={onSortChange}
					/>
				</div>
				<div className={classes.filterGridWrapper}>
					<ProductFilters
						block={block}
						availableFilters={availableFilters}
						currentFilters={currentFilters}
						filters={prepareFilters(availableFilters, session.filters, prodListData, true)}
						categories={categoryMenu}
						onFilterChange={onFilterChange}
						category={category}
						isOpen={filterOpen}
					/>
					<Container className={classes.gridWrapper} disableGutters>
						{productListing.total > productListing.limit ? <ProductInfiniteScroll
							currentPage={pagination.currentPage}
							loadedPages={pagination.loadedPages}
							loadedProducts={products.length}
							totalProducts={productListing.total}
							productsPerPage={productListing.limit}
							changePage={onPageChange}
							position={'top'}
						/> : ''}
						<ProductGrid
							products={products}
							productListing={productListing}
							breadcrumbs={breadcrumbs}
							list={{name: 'product_listing_page', id: block.id, location: router.asPath}}
							salesChannelId={session.salesChannel ? session.salesChannel.id : ''}
							lazyHydrate={initial}
							paramsHash={paramsHash()}
						/>
						<ProductInfiniteScroll
							currentPage={pagination.currentPage}
							loadedPages={pagination.loadedPages}
							loadedProducts={products.length}
							totalProducts={productListing.total}
							productsPerPage={productListing.limit}
							changePage={onPageChange}
							position={'bottom'}
						/>
						<BottomSection category={category} categoryMenu={categoryMenu}/>
					</Container>
				</div>
			</div>

		</>
	);
}

export default StandardPLP;
